/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/BoardPortal/ConsolidatedFinancials.tsx
 * PURPOSE: Board Consolidated Financials Dashboard - EPIC 10.3 One-click Board report.
 *          An API failure shows an error, never placeholder P&L figures (audit A2, #1355).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: One-click Board report with <2 day generation (vs current 5-7 days)
 *
 * @ai-human-orchestration
 * - AIRole: Mid Backend Developer
 * - HumanHandoff: Local Team validates Bflow R2R integration
 * - CollaborationLevel: Remote Team implements, CFO validates report format
 *
 * @business-context
 * - Domain: Vietnamese F&B Consolidated Financial Reporting
 * - Impact: IPO Critical - Board needs monthly P&L consolidation
 * - Stakeholders: Enterprise CFO, Board, Multi-BU Operators
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Dashboard Page
 * - Pattern: React Component, Data Fetching, Financial Visualization
 * - Dependencies: React 18, TypeScript, Axios
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  *
 * Sprint 10 EPIC 10.3: Consolidated Financial Reporting (4 SP)
 * Features:
 * - Consolidated P&L statement (5 BUs + NQDL + Holdings)
 * - BU performance comparison (revenue, profit margin, labor cost %)
 * - Key financial metrics (ROA, ROE, labor cost %, EBITDA)
 * - Trend analysis (12 months)
 * - One-click monthly Board report export (PDF + Excel)
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '@/services/api/config';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import { getTenantDisplayText } from '../../config/tenant';
import {
  consolidatedFinancialsService,
  ConsolidatedFinancialsResponse,
  BUComparisonResponse,
  BoardReportResponse,
  formatVND,
  formatVNDBillion,
  formatPercent,
  getMarginStatusColor,
  getLaborCostStatusDisplay,
  formatVietnameseReportMonth,
  getRankingBadgeColor,
} from '../../services/api/consolidatedFinancialsService';

// ============= Component State Types =============

interface DashboardState {
  activeTab: 'consolidated' | 'comparison' | 'board-report';
  selectedMonth: string;
}

// ============= Board Consolidated Financials Dashboard Component =============

export const ConsolidatedFinancials: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Dashboard state
  const [state, setState] = useState<DashboardState>({
    activeTab: 'consolidated',
    selectedMonth: new Date().toISOString().slice(0, 7), // YYYY-MM
  });

  // Data state
  const [consolidated, setConsolidated] = useState<ConsolidatedFinancialsResponse | null>(null);
  const [comparison, setComparison] = useState<BUComparisonResponse | null>(null);
  const [boardReport, setBoardReport] = useState<BoardReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch data on mount and state changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [consolidatedData, comparisonData, reportData] = await Promise.all([
          consolidatedFinancialsService.getConsolidated(),
          consolidatedFinancialsService.getBUComparison(),
          consolidatedFinancialsService.getBoardReport(state.selectedMonth),
        ]);

        setConsolidated(consolidatedData);
        setComparison(comparisonData);
        setBoardReport(reportData);
      } catch (err) {
        console.warn('Financial API unavailable:', err instanceof Error ? err.message : err);
        setError('Không tải được dữ liệu tài chính hợp nhất từ máy chủ. Vui lòng thử lại sau.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [state.selectedMonth]);

  // Export handlers - SPRINT 10 EPIC 10.3
  const handleExportPDF = () => {
    // PDF export uses same CSV endpoint for now (can open in Excel/Google Sheets)
    handleExportExcel();
  };

  const handleExportExcel = async () => {
    try {
      // Build export URL with current month filter
      const params = new URLSearchParams();
      if (state.selectedMonth) {
        params.append('report_month', state.selectedMonth);
      }

      // Direct download via browser
      const exportUrl = `${API_BASE_URL}/api/v1/analytics/consolidated-financials/export?${params.toString()}`;

      // Create hidden link and trigger download
      const link = document.createElement('a');
      link.href = exportUrl;
      link.setAttribute('download', `board_report_${state.selectedMonth || new Date().toISOString().slice(0,7)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Lỗi khi xuất báo cáo. Vui lòng thử lại.');
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Đang tải dữ liệu tài chính hợp nhất...</p>
        </div>
      </div>
    );
  }

  // Error state: say what failed; never show placeholder P&L figures (audit A2, #1355)
  if (error) {
    return (
      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerLeft}>
              <h1 style={styles.title}>📊 Board Portal - Báo Cáo Tài Chính Hợp Nhất</h1>
              <div style={styles.roleBadge}>Board View</div>
            </div>
            <div style={styles.headerRight}>
              <button style={styles.logoutButton} onClick={() => logout()}>Đăng xuất</button>
            </div>
          </div>
        </header>
        <main style={styles.main}>
          <section style={styles.tableSection} role="alert">
            <h2 style={styles.sectionTitle}>Lỗi tải dữ liệu</h2>
            <p>{error}</p>
          </section>
        </main>
      </div>
    );
  }

  // DD-0067 / #1355: no real financial data yet. Zeroed P&L rows under one
  // company's unit names would read as real figures; show an empty state.
  if (consolidated?.data_status === 'PENDING_REAL_DATA') {
    return (
      <div style={styles.container}>
        <main style={styles.main}>
          <section style={styles.tableSection} role="status">
            <h2 style={styles.sectionTitle}>Chưa có dữ liệu tài chính hợp nhất</h2>
            <p>{consolidated.data_status_message_vi}</p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.titleBadge}>
              <h1 style={styles.title}>📊 Board Portal - Báo Cáo Tài Chính Hợp Nhất</h1>
            </div>
            <div style={styles.roleBadge}>Board View</div>
          </div>

          <div style={styles.headerRight}>
            <div style={styles.dateInfo}>
              <div style={styles.dateLabel}>
                {new Date().toLocaleDateString('vi-VN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <div style={styles.userName}>{user?.full_name || 'Board Member'}</div>
            </div>
            <div style={styles.exportButtons}>
              <button style={styles.exportButtonPDF} onClick={handleExportPDF}>
                📄 Xuất PDF
              </button>
              <button style={styles.exportButtonExcel} onClick={handleExportExcel}>
                📊 Xuất Excel
              </button>
            </div>
            <button style={styles.logoutButton} onClick={() => logout()}>
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        {/* Corporate Structure Banner */}
        <section style={styles.structureBanner}>
          <div style={styles.structureItem}>
            <span style={styles.structureIcon}>🏛️</span>
            <div>
              <div style={styles.structureLabel}>Công ty mẹ</div>
              <div style={styles.structureValue}>{consolidated?.company || '—'}</div>
            </div>
          </div>
          <span style={styles.structureArrow}>→</span>
          <div style={styles.structureItem}>
            <span style={styles.structureIcon}>🏢</span>
            <div>
              <div style={styles.structureLabel}>Công ty con</div>
              <div style={styles.structureValue}>{consolidated?.subsidiary || '—'}</div>
            </div>
          </div>
          <span style={styles.structureArrow}>→</span>
          <div style={styles.structureItem}>
            <span style={styles.structureIcon}>🏪</span>
            <div>
              <div style={styles.structureLabel}>Đơn vị kinh doanh</div>
              <div style={styles.structureValue}>{consolidated?.bus_count ?? 0} BUs</div>
            </div>
          </div>
        </section>

        {/* Month Selector */}
        <div style={styles.monthSelector}>
          <label style={styles.monthLabel}>Tháng báo cáo:</label>
          <input
            type="month"
            style={styles.monthInput}
            value={state.selectedMonth}
            onChange={(e) => setState({ ...state, selectedMonth: e.target.value })}
          />
        </div>

        {/* Tabs */}
        <div style={styles.tabsContainer}>
          {[
            { id: 'consolidated', label: '📋 P&L Hợp Nhất' },
            { id: 'comparison', label: '📊 So Sánh BU' },
            { id: 'board-report', label: '📈 Báo Cáo Board' },
          ].map((tab) => (
            <button
              key={tab.id}
              style={{
                ...styles.tabButton,
                ...(state.activeTab === tab.id ? styles.tabButtonActive : {}),
              }}
              onClick={() => setState({ ...state, activeTab: tab.id as DashboardState['activeTab'] })}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Consolidated P&L Tab */}
        {state.activeTab === 'consolidated' && consolidated && (
          <section style={styles.tabContent}>
            {/* Consolidated Summary */}
            <div style={styles.consolidatedSummary}>
              <h3 style={styles.sectionTitle}>
                💰 Báo Cáo P&L Hợp Nhất - {consolidated.company}
              </h3>

              {/* KPI Cards */}
              <div style={styles.kpiGrid}>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiIcon}>💵</div>
                  <div style={styles.kpiContent}>
                    <div style={styles.kpiLabel}>Doanh Thu Hợp Nhất</div>
                    <div style={styles.kpiValue}>
                      {formatVND(consolidated.consolidated.consolidated_pnl.revenue)}
                    </div>
                  </div>
                </div>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiIcon}>📈</div>
                  <div style={styles.kpiContent}>
                    <div style={styles.kpiLabel}>Lợi Nhuận Gộp</div>
                    <div style={styles.kpiValue}>
                      {formatVND(consolidated.consolidated.consolidated_pnl.gross_profit)}
                    </div>
                    <div style={styles.kpiSubValue}>
                      Biên LN: {formatPercent(consolidated.consolidated.consolidated_pnl.gross_margin)}
                    </div>
                  </div>
                </div>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiIcon}>💰</div>
                  <div style={styles.kpiContent}>
                    <div style={styles.kpiLabel}>Lợi Nhuận Thuần</div>
                    <div
                      style={{
                        ...styles.kpiValue,
                        color: getMarginStatusColor(consolidated.consolidated.consolidated_pnl.net_margin),
                      }}
                    >
                      {formatVND(consolidated.consolidated.consolidated_pnl.net_profit)}
                    </div>
                    <div style={styles.kpiSubValue}>
                      Biên LN: {formatPercent(consolidated.consolidated.consolidated_pnl.net_margin)}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    ...styles.kpiCard,
                    borderColor: consolidated.consolidated.kpis.is_within_target ? '#22c55e' : '#ef4444',
                  }}
                >
                  <div style={styles.kpiIcon}>👥</div>
                  <div style={styles.kpiContent}>
                    <div style={styles.kpiLabel}>Chi Phí Nhân Công</div>
                    <div
                      style={{
                        ...styles.kpiValue,
                        color: consolidated.consolidated.kpis.is_within_target ? '#22c55e' : '#ef4444',
                      }}
                    >
                      {formatPercent(consolidated.consolidated.kpis.labor_cost_ratio)}
                    </div>
                    <div style={styles.kpiSubValue}>
                      Mục tiêu: &lt;{formatPercent(consolidated.consolidated.kpis.labor_cost_target)}
                    </div>
                  </div>
                </div>
              </div>

              {/* P&L Table */}
              <div style={styles.pnlTable}>
                <h4 style={styles.tableTitle}>📋 Báo Cáo Kết Quả Kinh Doanh</h4>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Khoản mục</th>
                        <th style={{ ...styles.th, textAlign: 'right' as const }}>Số tiền (VND)</th>
                        <th style={{ ...styles.th, textAlign: 'right' as const }}>% Doanh thu</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={styles.tr}>
                        <td style={styles.td}>Doanh thu thuần</td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          <strong>{formatVND(consolidated.consolidated.consolidated_pnl.revenue)}</strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>100.0%</td>
                      </tr>
                      <tr style={styles.tr}>
                        <td style={styles.td}>Giá vốn hàng bán</td>
                        <td style={{ ...styles.td, textAlign: 'right' as const, color: '#ef4444' }}>
                          ({formatVND(consolidated.consolidated.consolidated_pnl.cogs)})
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          {formatPercent(
                            (consolidated.consolidated.consolidated_pnl.cogs /
                              consolidated.consolidated.consolidated_pnl.revenue) *
                              100
                          )}
                        </td>
                      </tr>
                      <tr style={{ ...styles.tr, background: 'rgba(34, 197, 94, 0.1)' }}>
                        <td style={styles.td}>
                          <strong>Lợi nhuận gộp</strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          <strong>{formatVND(consolidated.consolidated.consolidated_pnl.gross_profit)}</strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          <strong>{formatPercent(consolidated.consolidated.consolidated_pnl.gross_margin)}</strong>
                        </td>
                      </tr>
                      <tr style={styles.tr}>
                        <td style={styles.td}>Chi phí nhân công</td>
                        <td style={{ ...styles.td, textAlign: 'right' as const, color: '#ef4444' }}>
                          ({formatVND(consolidated.consolidated.consolidated_pnl.labor_cost)})
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          {formatPercent(consolidated.consolidated.kpis.labor_cost_ratio)}
                        </td>
                      </tr>
                      <tr style={styles.tr}>
                        <td style={styles.td}>Chi phí hoạt động</td>
                        <td style={{ ...styles.td, textAlign: 'right' as const, color: '#ef4444' }}>
                          ({formatVND(consolidated.consolidated.consolidated_pnl.operating_expenses)})
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          {formatPercent(
                            (consolidated.consolidated.consolidated_pnl.operating_expenses /
                              consolidated.consolidated.consolidated_pnl.revenue) *
                              100
                          )}
                        </td>
                      </tr>
                      <tr style={{ ...styles.tr, background: 'rgba(59, 130, 246, 0.1)' }}>
                        <td style={styles.td}>
                          <strong>Lợi nhuận từ HĐKD</strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          <strong>{formatVND(consolidated.consolidated.consolidated_pnl.operating_profit)}</strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          <strong>{formatPercent(consolidated.consolidated.consolidated_pnl.operating_margin)}</strong>
                        </td>
                      </tr>
                      <tr style={styles.tr}>
                        <td style={styles.td}>Thu nhập khác</td>
                        <td style={{ ...styles.td, textAlign: 'right' as const, color: '#22c55e' }}>
                          {formatVND(consolidated.consolidated.consolidated_pnl.other_income)}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>-</td>
                      </tr>
                      <tr style={styles.tr}>
                        <td style={styles.td}>Chi phí khác</td>
                        <td style={{ ...styles.td, textAlign: 'right' as const, color: '#ef4444' }}>
                          ({formatVND(consolidated.consolidated.consolidated_pnl.other_expenses)})
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>-</td>
                      </tr>
                      <tr style={{ ...styles.tr, background: 'rgba(234, 179, 8, 0.1)' }}>
                        <td style={styles.td}>
                          <strong>LỢI NHUẬN THUẦN</strong>
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            textAlign: 'right' as const,
                            color: getMarginStatusColor(consolidated.consolidated.consolidated_pnl.net_margin),
                            fontSize: '1.125rem',
                          }}
                        >
                          <strong>{formatVND(consolidated.consolidated.consolidated_pnl.net_profit)}</strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' as const }}>
                          <strong>{formatPercent(consolidated.consolidated.consolidated_pnl.net_margin)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Intercompany Eliminations */}
              {consolidated.consolidated.intercompany_eliminations.entries.length > 0 && (
                <div style={styles.eliminationsSection}>
                  <h4 style={styles.tableTitle}>🔄 Loại Trừ Giao Dịch Nội Bộ</h4>
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Từ</th>
                          <th style={styles.th}>Đến</th>
                          <th style={styles.th}>Mô tả</th>
                          <th style={styles.th}>Loại</th>
                          <th style={{ ...styles.th, textAlign: 'right' as const }}>Số tiền</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consolidated.consolidated.intercompany_eliminations.entries.map((entry, idx) => (
                          <tr key={idx} style={styles.tr}>
                            <td style={styles.td}>{entry.from_entity}</td>
                            <td style={styles.td}>{entry.to_entity}</td>
                            <td style={styles.td}>{entry.description}</td>
                            <td style={styles.td}>
                              <span style={styles.eliminationTypeBadge}>{entry.elimination_type}</span>
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' as const }}>{formatVND(entry.amount)}</td>
                          </tr>
                        ))}
                        <tr style={{ ...styles.tr, background: 'rgba(255, 255, 255, 0.05)' }}>
                          <td colSpan={4} style={{ ...styles.td, textAlign: 'right' as const }}>
                            <strong>Tổng loại trừ:</strong>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right' as const }}>
                            <strong>
                              {formatVND(consolidated.consolidated.intercompany_eliminations.total)}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* BU Breakdown */}
              <div style={styles.buBreakdown}>
                <h4 style={styles.tableTitle}>🏪 Chi Tiết Theo Đơn Vị Kinh Doanh</h4>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>BU</th>
                        <th style={{ ...styles.th, textAlign: 'right' as const }}>Doanh Thu</th>
                        <th style={{ ...styles.th, textAlign: 'right' as const }}>LN Gộp</th>
                        <th style={{ ...styles.th, textAlign: 'right' as const }}>Biên LN Gộp</th>
                        <th style={{ ...styles.th, textAlign: 'right' as const }}>LN Thuần</th>
                        <th style={{ ...styles.th, textAlign: 'right' as const }}>Biên LN Thuần</th>
                        <th style={{ ...styles.th, textAlign: 'right' as const }}>CP Nhân Công</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consolidated.consolidated.bu_breakdown.map((bu) => (
                        <tr key={bu.bu_id} style={styles.tr}>
                          <td style={styles.td}>
                            <strong>{bu.bu_name}</strong>
                            <span style={styles.buCode}> ({bu.bu_code})</span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right' as const }}>{formatVND(bu.pnl.revenue)}</td>
                          <td style={{ ...styles.td, textAlign: 'right' as const }}>{formatVND(bu.pnl.gross_profit)}</td>
                          <td
                            style={{
                              ...styles.td,
                              textAlign: 'right' as const,
                              color: getMarginStatusColor(bu.pnl.gross_margin),
                            }}
                          >
                            {formatPercent(bu.pnl.gross_margin)}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right' as const }}>{formatVND(bu.pnl.net_profit)}</td>
                          <td
                            style={{
                              ...styles.td,
                              textAlign: 'right' as const,
                              color: getMarginStatusColor(bu.pnl.net_margin),
                            }}
                          >
                            {formatPercent(bu.pnl.net_margin)}
                          </td>
                          <td
                            style={{
                              ...styles.td,
                              textAlign: 'right' as const,
                              color: bu.kpis.is_within_target ? '#22c55e' : '#ef4444',
                            }}
                          >
                            {formatPercent(bu.kpis.labor_cost_ratio)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* BU Comparison Tab */}
        {state.activeTab === 'comparison' && comparison && (
          <section style={styles.tabContent}>
            <h3 style={styles.sectionTitle}>📊 So Sánh Hiệu Quả Các Đơn Vị Kinh Doanh</h3>

            {/* Summary */}
            <div style={styles.comparisonSummary}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>🏆</div>
                <div style={styles.summaryContent}>
                  <div style={styles.summaryLabel}>Doanh Thu Cao Nhất</div>
                  <div style={styles.summaryValue}>{comparison.summary.best_revenue || '-'}</div>
                </div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>📈</div>
                <div style={styles.summaryContent}>
                  <div style={styles.summaryLabel}>Biên LN Cao Nhất</div>
                  <div style={styles.summaryValue}>{comparison.summary.best_margin || '-'}</div>
                </div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>⚡</div>
                <div style={styles.summaryContent}>
                  <div style={styles.summaryLabel}>Hiệu Quả Nhất</div>
                  <div style={styles.summaryValue}>{comparison.summary.most_efficient || '-'}</div>
                </div>
              </div>
            </div>

            {/* Rankings */}
            <div style={styles.rankingsGrid}>
              {/* Revenue Ranking */}
              <div style={styles.rankingCard}>
                <h4 style={styles.rankingTitle}>💵 Xếp Hạng Doanh Thu</h4>
                <div style={styles.rankingList}>
                  {comparison.rankings.by_revenue.map((item) => (
                    <div key={item.rank} style={styles.rankingItem}>
                      <span
                        style={{
                          ...styles.rankBadge,
                          background: getRankingBadgeColor(item.rank),
                        }}
                      >
                        #{item.rank}
                      </span>
                      <span style={styles.rankBU}>{item.bu}</span>
                      <span style={styles.rankValue}>{formatVND(item.revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Profit Margin Ranking */}
              <div style={styles.rankingCard}>
                <h4 style={styles.rankingTitle}>📈 Xếp Hạng Biên Lợi Nhuận</h4>
                <div style={styles.rankingList}>
                  {comparison.rankings.by_profit_margin.map((item) => (
                    <div key={item.rank} style={styles.rankingItem}>
                      <span
                        style={{
                          ...styles.rankBadge,
                          background: getRankingBadgeColor(item.rank),
                        }}
                      >
                        #{item.rank}
                      </span>
                      <span style={styles.rankBU}>{item.bu}</span>
                      <span style={styles.rankValue}>{formatPercent(item.net_margin)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Labor Efficiency Ranking */}
              <div style={styles.rankingCard}>
                <h4 style={styles.rankingTitle}>⚡ Xếp Hạng Hiệu Quả Chi Phí NC</h4>
                <div style={styles.rankingList}>
                  {comparison.rankings.by_labor_efficiency.map((item) => (
                    <div key={item.rank} style={styles.rankingItem}>
                      <span
                        style={{
                          ...styles.rankBadge,
                          background: getRankingBadgeColor(item.rank),
                        }}
                      >
                        #{item.rank}
                      </span>
                      <span style={styles.rankBU}>{item.bu}</span>
                      <span style={styles.rankValue}>{formatPercent(item.labor_cost_ratio)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Board Report Tab */}
        {state.activeTab === 'board-report' && boardReport && (
          <section style={styles.tabContent}>
            <div style={styles.boardReportHeader}>
              <h3 style={styles.sectionTitle}>
                📈 Báo Cáo HĐQT - {formatVietnameseReportMonth(boardReport.report_month)}
              </h3>
              <div style={styles.generationInfo}>
                <span>Thời gian tạo: {boardReport.generation_time}</span>
                <span style={styles.generationTarget}>(Mục tiêu: &lt;2 ngày)</span>
              </div>
            </div>

            {/* Executive Summary */}
            <div style={styles.executiveSummary}>
              <h4 style={styles.summaryTitle}>📋 Tóm Tắt Điều Hành</h4>

              <div style={styles.execKpiGrid}>
                <div style={styles.execKpiCard}>
                  <div style={styles.execKpiLabel}>Tổng Doanh Thu</div>
                  <div style={styles.execKpiValue}>
                    {formatVND(boardReport.executive_summary.total_revenue)}
                  </div>
                </div>
                <div style={styles.execKpiCard}>
                  <div style={styles.execKpiLabel}>Lợi Nhuận Thuần</div>
                  <div style={styles.execKpiValue}>
                    {formatVND(boardReport.executive_summary.net_profit)}
                  </div>
                </div>
                <div style={styles.execKpiCard}>
                  <div style={styles.execKpiLabel}>Biên Lợi Nhuận</div>
                  <div
                    style={{
                      ...styles.execKpiValue,
                      color: getMarginStatusColor(boardReport.executive_summary.net_margin),
                    }}
                  >
                    {formatPercent(boardReport.executive_summary.net_margin)}
                  </div>
                </div>
                <div style={styles.execKpiCard}>
                  <div style={styles.execKpiLabel}>Chi Phí Nhân Công</div>
                  <div
                    style={{
                      ...styles.execKpiValue,
                      color:
                        boardReport.executive_summary.labor_cost_status === 'within_target'
                          ? '#22c55e'
                          : '#ef4444',
                    }}
                  >
                    {formatPercent(boardReport.executive_summary.labor_cost_ratio)}
                  </div>
                  <div
                    style={{
                      ...styles.laborStatusBadge,
                      ...getLaborCostStatusDisplay(boardReport.executive_summary.labor_cost_status),
                    }}
                  >
                    {getLaborCostStatusDisplay(boardReport.executive_summary.labor_cost_status).icon}{' '}
                    {getLaborCostStatusDisplay(boardReport.executive_summary.labor_cost_status).text}
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div style={styles.keyMetrics}>
                <h5 style={styles.keyMetricsTitle}>📊 Chỉ Số Tài Chính Chính</h5>
                <div style={styles.keyMetricsGrid}>
                  <div style={styles.metricItem}>
                    <span style={styles.metricLabel}>ROA (Return on Assets)</span>
                    <span style={styles.metricValue}>{boardReport.key_metrics.roa}%</span>
                  </div>
                  <div style={styles.metricItem}>
                    <span style={styles.metricLabel}>ROE (Return on Equity)</span>
                    <span style={styles.metricValue}>{boardReport.key_metrics.roe}%</span>
                  </div>
                  <div style={styles.metricItem}>
                    <span style={styles.metricLabel}>EBITDA</span>
                    <span style={styles.metricValue}>{formatVND(boardReport.key_metrics.ebitda)}</span>
                  </div>
                </div>
              </div>

              {/* Highlights */}
              <div style={styles.highlights}>
                <h5 style={styles.highlightsTitle}>🎯 Điểm Nổi Bật</h5>
                <ul style={styles.highlightsList}>
                  {boardReport.highlights.map((highlight, idx) => (
                    <li key={idx} style={styles.highlightItem}>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Top Performers */}
              <div style={styles.topPerformers}>
                <div style={styles.performerCard}>
                  <span style={styles.performerIcon}>🏆</span>
                  <div>
                    <div style={styles.performerLabel}>BU Doanh Thu Cao Nhất</div>
                    <div style={styles.performerValue}>
                      {boardReport.executive_summary.top_performer || '-'}
                    </div>
                  </div>
                </div>
                <div style={styles.performerCard}>
                  <span style={styles.performerIcon}>⚡</span>
                  <div>
                    <div style={styles.performerLabel}>BU Hiệu Quả Nhất</div>
                    <div style={styles.performerValue}>
                      {boardReport.executive_summary.most_efficient_bu || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <span>Sprint 10 EPIC 10.3 - Board Consolidated Financials</span>
          <span>{getTenantDisplayText()}</span>
        </div>
      </footer>
    </div>
  );
};

// ============= Styles =============

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: '#fff',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '1rem',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid rgba(255,255,255,0.2)',
    borderTopColor: '#667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '1.125rem',
    opacity: 0.8,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '1rem',
  },
  errorIcon: {
    fontSize: '3rem',
  },
  errorText: {
    fontSize: '1.25rem',
    color: '#ef4444',
  },
  retryButton: {
    padding: '0.75rem 1.5rem',
    background: '#667eea',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  header: {
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    padding: '1rem 0',
    position: 'sticky' as const,
    top: 0,
    zIndex: 100,
  },
  headerContent: {
    maxWidth: '1600px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '1rem',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  titleBadge: {
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    padding: '0.5rem 1.5rem',
    borderRadius: '12px',
  },
  title: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 'bold',
  },
  roleBadge: {
    padding: '0.375rem 0.75rem',
    background: 'rgba(102, 126, 234, 0.2)',
    border: '1px solid #667eea',
    borderRadius: '6px',
    fontSize: '0.75rem',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap' as const,
  },
  dateInfo: {
    textAlign: 'right' as const,
  },
  dateLabel: {
    fontSize: '0.75rem',
    opacity: 0.7,
  },
  userName: {
    fontSize: '1rem',
    fontWeight: '600',
  },
  exportButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  exportButtonPDF: {
    padding: '0.5rem 1rem',
    background: 'rgba(239, 68, 68, 0.2)',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.75rem',
  },
  exportButtonExcel: {
    padding: '0.5rem 1rem',
    background: 'rgba(34, 197, 94, 0.2)',
    border: '1px solid #22c55e',
    borderRadius: '8px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.75rem',
  },
  logoutButton: {
    padding: '0.5rem 1rem',
    background: 'rgba(239, 68, 68, 0.2)',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  main: {
    maxWidth: '1600px',
    margin: '0 auto',
    padding: '2rem',
  },
  structureBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    padding: '1.5rem',
    background: 'rgba(102, 126, 234, 0.1)',
    border: '1px solid rgba(102, 126, 234, 0.3)',
    borderRadius: '12px',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
  },
  structureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  structureIcon: {
    fontSize: '1.5rem',
  },
  structureLabel: {
    fontSize: '0.7rem',
    opacity: 0.7,
  },
  structureValue: {
    fontWeight: '600',
    fontSize: '0.875rem',
  },
  structureArrow: {
    fontSize: '1.25rem',
    opacity: 0.5,
  },
  monthSelector: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  monthLabel: {
    fontSize: '0.875rem',
  },
  monthInput: {
    padding: '0.5rem 1rem',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '0.875rem',
  },
  tabsContainer: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    paddingBottom: '1rem',
    flexWrap: 'wrap' as const,
  },
  tabButton: {
    padding: '0.75rem 1.5rem',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    transition: 'all 0.2s',
  },
  tabButtonActive: {
    background: '#667eea',
    borderColor: '#667eea',
  },
  tabContent: {
    animation: 'fadeIn 0.3s ease',
  },
  sectionTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.25rem',
  },
  consolidatedSummary: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: '1.5rem',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  kpiCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  kpiIcon: {
    fontSize: '2rem',
  },
  kpiContent: {
    flex: 1,
  },
  kpiLabel: {
    fontSize: '0.75rem',
    opacity: 0.8,
  },
  kpiValue: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
  },
  kpiSubValue: {
    fontSize: '0.7rem',
    opacity: 0.7,
    marginTop: '0.25rem',
  },
  pnlTable: {
    marginBottom: '2rem',
  },
  tableTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
  },
  tableContainer: {
    overflowX: 'auto' as const,
    borderRadius: '8px',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left' as const,
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    fontSize: '0.75rem',
    fontWeight: '600',
    background: 'rgba(0, 0, 0, 0.3)',
  },
  tr: {
    transition: 'background 0.2s',
  },
  td: {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    fontSize: '0.8rem',
  },
  eliminationsSection: {
    marginBottom: '2rem',
  },
  eliminationTypeBadge: {
    padding: '0.25rem 0.5rem',
    background: 'rgba(102, 126, 234, 0.2)',
    color: '#667eea',
    borderRadius: '4px',
    fontSize: '0.7rem',
  },
  buBreakdown: {
    marginBottom: '2rem',
  },
  buCode: {
    fontSize: '0.7rem',
    opacity: 0.7,
  },
  comparisonSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  summaryCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  summaryIcon: {
    fontSize: '2rem',
  },
  summaryContent: {
    flex: 1,
  },
  summaryLabel: {
    fontSize: '0.75rem',
    opacity: 0.8,
  },
  summaryValue: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
  },
  rankingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
  },
  rankingCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: '1.5rem',
  },
  rankingTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
  },
  rankingList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  rankingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.75rem',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
  },
  rankBadge: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontWeight: 'bold',
    fontSize: '0.75rem',
  },
  rankBU: {
    flex: 1,
    fontWeight: '500',
  },
  rankValue: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
  },
  boardReportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap' as const,
    gap: '1rem',
  },
  generationInfo: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    fontSize: '0.875rem',
  },
  generationTarget: {
    opacity: 0.7,
    fontSize: '0.75rem',
  },
  executiveSummary: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: '1.5rem',
  },
  summaryTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.125rem',
  },
  execKpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  execKpiCard: {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '12px',
    padding: '1.25rem',
    textAlign: 'center' as const,
  },
  execKpiLabel: {
    fontSize: '0.75rem',
    opacity: 0.8,
    marginBottom: '0.5rem',
  },
  execKpiValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  laborStatusBadge: {
    marginTop: '0.5rem',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.7rem',
    display: 'inline-block',
  },
  keyMetrics: {
    marginBottom: '2rem',
  },
  keyMetricsTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
  },
  keyMetricsGrid: {
    display: 'flex',
    gap: '2rem',
    flexWrap: 'wrap' as const,
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  metricLabel: {
    fontSize: '0.75rem',
    opacity: 0.8,
  },
  metricValue: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#667eea',
  },
  highlights: {
    marginBottom: '2rem',
  },
  highlightsTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
  },
  highlightsList: {
    margin: 0,
    padding: '0 0 0 1.5rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  highlightItem: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  topPerformers: {
    display: 'flex',
    gap: '1.5rem',
    flexWrap: 'wrap' as const,
  },
  performerCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    background: 'rgba(234, 179, 8, 0.1)',
    border: '1px solid rgba(234, 179, 8, 0.3)',
    borderRadius: '12px',
    padding: '1rem 1.5rem',
  },
  performerIcon: {
    fontSize: '1.5rem',
  },
  performerLabel: {
    fontSize: '0.75rem',
    opacity: 0.8,
  },
  performerValue: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  footer: {
    background: 'rgba(0, 0, 0, 0.3)',
    padding: '1rem 2rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  footerContent: {
    maxWidth: '1600px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    opacity: 0.7,
  },
};

export default ConsolidatedFinancials;
